import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { CheckCircle } from 'lucide-react';
import ReportIssueForm from './ReportIssueForm';
import apiClient from '../../utils/apiClient';
import './Support.scss';

/**
 * ReportIssueModal — Fullscreen-capable modal for submitting support tickets.
 * Captures automatic context (user, browser, page URL).
 */
const ReportIssueModal = ({ show, onHide, errorPayload, isTenantAdmin, institutes, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (formData) => {
    setSubmitting(true);

    try {
      let screenshotUrl = '';

      // Upload screenshot via server-side upload if provided
      if (formData.screenshot) {
        try {
          const fd = new FormData();
          fd.append('file', formData.screenshot);
          const { data: uploadData } = await apiClient.post('/support/screenshot-url', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          screenshotUrl = uploadData.key;
        } catch {
          // Proceed without screenshot if upload fails
        }
      }

      const payload = {
        category: formData.category,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        pageUrl: window.location.pathname + window.location.search,
        browser: navigator.userAgent,
        os: navigator.platform,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        appVersion: process.env.REACT_APP_VERSION || '1.0.0',
        screenshotUrl,
      };

      // tenantadmin creates on behalf of an institute
      if (isTenantAdmin && formData.onBehalfOfInstituteId) {
        payload.onBehalfOfInstituteId = formData.onBehalfOfInstituteId;
      }

      const { data } = await apiClient.post('/support/report', payload);

      const ticketId = data.ticketId || data.ticket?.ticketId || 'SUP-XXXX';
      setSuccess({ ticketId });
      if (onSuccess) onSuccess();
    } catch {
      // Error handled by apiClient interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSuccess(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="report-modal">
      <Modal.Header closeButton>
        <Modal.Title>{success ? 'Ticket Submitted' : 'Report an Issue'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {success ? (
          <div className="report-success">
            <div className="report-success__icon">
              <CheckCircle size={48} />
            </div>
            <div className="report-success__title">Ticket Created Successfully</div>
            <div className="report-success__ticket-id">Ticket ID: {success.ticketId}</div>
          </div>
        ) : (
          <ReportIssueForm
            onSubmit={handleSubmit}
            submitting={submitting}
            errorPayload={errorPayload}
            isTenantAdmin={isTenantAdmin}
            institutes={institutes}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ReportIssueModal;
